import { useCallback, useEffect, useState } from "react";
import useFirestore from "hooks/useFirestore";
import useWebotServer from "hooks/useWebotServer";
import type { REST_schema } from "../../../../../../@types/sharedSchema";

/**
 * The contact's PENDING scheduled template sends (one_off campaigns touching this phone) — the
 * data behind the "הודעות מתוזמנות" section and its cancel/reschedule actions. REST, not a
 * listener: pending one_offs per contact are few, and the surface refreshes after every action.
 * Silently empty for accounts without officialWhatsapp (the endpoint is feature-gated).
 */
export function useContactScheduledSends(contactId: string | undefined): {
  sends: REST_schema.WhatsappScheduledSendView[];
  loading: boolean;
  refresh: () => void;
  cancelSend: (campaignId: string) => Promise<void>;
  rescheduleSend: (campaignId: string, scheduledForIso: string) => Promise<void>;
} {
  const { userSettings } = useFirestore();
  const { whatsappScheduledSendsQuery, whatsappScheduledSendCancel, whatsappScheduledSendReschedule } = useWebotServer();
  const hasOfficialWhatsapp = userSettings?.advancedFeatures?.includes("officialWhatsapp") ?? false;

  const [sends, setSends] = useState<REST_schema.WhatsappScheduledSendView[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!contactId || !hasOfficialWhatsapp) {
      setSends([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    whatsappScheduledSendsQuery({ contactId })
      .then((res) => {
        if (!cancelled) setSends(res.sends);
      })
      .catch(() => {
        if (!cancelled) setSends([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, hasOfficialWhatsapp, refreshTick]);

  const refresh = useCallback(() => setRefreshTick((n) => n + 1), []);

  const cancelSend = useCallback(
    async (campaignId: string) => {
      await whatsappScheduledSendCancel(campaignId);
      refresh();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refresh]
  );

  const rescheduleSend = useCallback(
    async (campaignId: string, scheduledForIso: string) => {
      await whatsappScheduledSendReschedule(campaignId, scheduledForIso);
      refresh();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refresh]
  );

  return { sends, loading, refresh, cancelSend, rescheduleSend };
}
